import React from 'react';
import { Container } from 'react-bootstrap';
import GradientHero from '../shared/GradientHero';
import '../common-styles.css';
import './TermsAndConditionsPage.css';

const TermsAndConditionsPage: React.FC = () => {
  return (
    <div className="tc-page">
      <GradientHero
        backTo="/"
        title={<span>Terms &amp; Conditions</span>}
        className="tc-hero"
        titleClassName="tc-hero-title"
      />

      <section className="page-frame tc-content" aria-label="Terms and Conditions">
        <Container>
          {/* 1. Binding Agreement */}
          <article className="tc-block" aria-labelledby="tc-h1">
            <h2 id="tc-h1" className="tc-heading">1. Binding Agreement</h2>
            <p>
              By accessing our website, you agree to be bound by these Terms &amp; Conditions (T&amp;C). This agreement
              supersedes any prior understandings or agreements (whether oral or written) between you and SunDiam
              regarding use of this platform. If there is any conflict between these terms and terms provided by
              affiliated entities, the affiliated terms will prevail for those interactions.
            </p>
          </article>

          {/* 2. Product Selection & Availability */}
          <article className="tc-block" aria-labelledby="tc-h2">
            <h2 id="tc-h2" className="tc-heading">2. Product Selection &amp; Availability</h2>
            <p>
              When you select a product for purchase, it constitutes an offer to buy, subject to availability. Please
              note that our inventory is updated regularly and the selected item may become unavailable due to
              simultaneous purchases by other customers. We appreciate your understanding in such cases.
            </p>
          </article>

          {/* 3. Modifications to Terms */}
          <article className="tc-block" aria-labelledby="tc-h3">
            <h2 id="tc-h3" className="tc-heading">3. Modifications to Terms</h2>
            <p>
              SunDiam reserves the right to update or revise these T&amp;C at any time without prior notice. Continued use
              of our services after changes have been made constitutes your acceptance of the modified terms.
            </p>
          </article>

          {/* 4. Website Ownership & Access */}
          <article className="tc-block" aria-labelledby="tc-h4">
            <h2 id="tc-h4" className="tc-heading">4. Website Ownership &amp; Access</h2>
            <ul>
              <li><strong>Website Ownership:</strong> This website is owned and operated by SunDiam.</li>
              <li><strong>Content Ownership:</strong> All content (text, graphics, images, software, logos, etc.) is the property of SunDiam.</li>
              <li><strong>Permitted Use:</strong> Content may be downloaded or printed solely for personal, non-commercial use. Modification or unauthorized use is prohibited.</li>
              <li><strong>Restricted Use:</strong> Commercial usage, redistribution, or reproduction of any content without written permission is strictly prohibited.</li>
              <li><strong>Legal Use Only:</strong> Users must not transmit unlawful, abusive, or obscene content via our platform.</li>
            </ul>
          </article>

          {/* 5. Account Responsibility */}
          <article className="tc-block" aria-labelledby="tc-h5">
            <h2 id="tc-h5" className="tc-heading">5. Account Responsibility</h2>
            <p>
              Users are responsible for maintaining the confidentiality of their login credentials. SunDiam is not liable
              for unauthorized transactions made using a user’s password.
            </p>
          </article>

          {/* 6. Pricing Policy */}
          <article className="tc-block" aria-labelledby="tc-h6">
            <h2 id="tc-h6" className="tc-heading">6. Pricing Policy</h2>
            <ul>
              <li><strong>Advance Payment:</strong> All purchases must be paid in full before dispatch.</li>
              <li><strong>Fixed Pricing:</strong> Prices are final upon order confirmation, but may be subject to correction in case of errors.</li>
              <li><strong>Currency:</strong> All transactions are conducted in US Dollars (USD).</li>
              <li><strong>Local Taxes:</strong> Any applicable local duties or taxes are the buyer’s responsibility.</li>
              <li><strong>Extra Services:</strong> Additional services (e.g. certification, customization) will incur separate charges.</li>
            </ul>
          </article>

          {/* 7. Shipping & Handling */}
          <article className="tc-block" aria-labelledby="tc-h7">
            <h2 id="tc-h7" className="tc-heading">7. Shipping &amp; Handling</h2>
            <h3 className="tc-subheading">Shipping Eligibility:</h3>
            <ul>
              <li><strong>Orders ≥ $25,000:</strong> Free shipping and handling.</li>
              <li><strong>Orders &lt; $25,000:</strong> A minimum flat fee of $100 will apply. (This may vary depending on the payment gateway and location.)</li>
              <li><strong>Courier Partners:</strong> Shipments will be sent through trusted logistics providers such as Brinks, Malca Amit, FedEx, or others, subject to destination and approval.</li>
              <li><strong>Insurance:</strong> All shipments are fully insured door-to-door by the courier service.</li>
              <li><strong>Shipping Restrictions:</strong> SunDiam reserves the right to decline or limit shipments to specific international destinations.</li>
            </ul>
          </article>

          {/* 8. Order Cancellation */}
          <article className="tc-block" aria-labelledby="tc-h8">
            <h2 id="tc-h8" className="tc-heading">8. Order Cancellation</h2>
            <ul>
              <li><strong>Reconfirmation:</strong> Orders are reconfirmed via our representative before final processing.</li>
              <li><strong>Cancellation Window:</strong> Orders cannot be cancelled after shipment.</li>
              <li><strong>Payment Deadline:</strong> Orders will automatically be cancelled if full payment is not received within five business days of order confirmation.</li>
            </ul>
          </article>

          {/* 9. KYC Compliance */}
          <article className="tc-block" aria-labelledby="tc-h9">
            <h2 id="tc-h9" className="tc-heading">9. KYC Compliance</h2>
            <p>All customers must complete Know Your Customer (KYC) procedures:</p>
            <ul>
              <li><strong>Individual Clients:</strong> Must provide government-issued photo ID and address proof.</li>
              <li><strong>Business Clients:</strong> Must submit business registration documents, tax IDs, and contact verification.</li>
              <li><strong>Data Use:</strong> All KYC data is securely stored and used only for compliance and verification purposes.</li>
            </ul>
          </article>

          {/* 10. Data Privacy & Retention */}
          <article className="tc-block" aria-labelledby="tc-h10">
            <h2 id="tc-h10" className="tc-heading">10. Data Privacy &amp; Retention</h2>
            <p>
              Your personal data is retained only for as long as necessary to fulfill the intended purpose or to comply
              with legal requirements. We may anonymize data for statistical purposes in which case it may be used
              indefinitely without further notice.
            </p>
          </article>

          {/* 11. Jurisdiction */}
          <article className="tc-block" aria-labelledby="tc-h11">
            <h2 id="tc-h11" className="tc-heading">11. Jurisdiction</h2>
            <p>
              All transactions and disputes are subject to the jurisdiction of the courts of Mumbai, India.
            </p>
          </article>

          <div className="tc-contact text-center">
            <h3 className="tc-contact-title">Contact Us</h3>
            <p className="tc-contact-note"><em>For any questions regarding these terms, please contact us at </em><a href="mailto:sundiam@ymail.com">sundiam@ymail.com</a></p>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default TermsAndConditionsPage;
